import { CommonModule } from '@angular/common';
import type { Signal } from '@angular/core';
import { Component, computed, input, signal } from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';
import { DynamicModule } from 'ng-dynamic-component';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import { DropdownComponent } from '@valk-nx/components/ui-dropdown/src/lib/dropdown';
import { InputComponent } from '@valk-nx/components/ui-input/src/lib/input';
import { LabelPosition } from '@valk-nx/components/ui-label/src/lib/label.interface';
import { LabelComponent } from '@valk-nx/components/ui-label/src/lib/label/label';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import { FooterContentDirective } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.directive';
import type {
	CtaType,
	LinkType,
	PersuadeCardInputs,
} from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.interface';
import { PRICING_TOOLTIP_OCCUPANCY } from '@valk-nx/core/lib/core';
import type { HeadingType } from '@valk-nx/core/lib/directives/heading/heading.type';
import type { PriceInterface } from '@valk-nx/core/lib/interfaces/price.interface';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';
import { ColumnsComponent } from '@valk-nx/storyblok/components/columns/src/lib/columns';
import { RichTextComponent } from '@valk-nx/storyblok/components/richtext/src/lib/rich-text';
import { StoryblokDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok.directive';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import type {
	Button,
	ButtonList,
	CardInterface,
	SbBlok,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import type {
	ExtendedPersuadeCardInputs,
	SortOption,
	SortType,
} from './searchable-list-section.interface';

const Components = {
	richText: RichTextComponent,
};

@Component({
	selector: 'sb-searchable-list-section',
	templateUrl: './searchable-list-section.html',
	imports: [
		AlertComponent,
		ColumnsComponent,
		CommonModule,
		DropdownComponent,
		DynamicModule,
		InputComponent,
		LabelComponent,
		PersuadeCardComponent,
		StoryblokDirective,
		TranslatePipe,
		FooterContentDirective,
		RichTextComponent,
	],
})
export class StoryblokSearchableListSectionComponent extends StoryblokRootDirective {
	title = input.required<SbBlok[] | string>();
	items = input<CardInterface[] | undefined>(undefined);
	hideFilter = input<boolean>(false);
	hidePriceFilter = input<boolean>(false);
	persuadeCards = input<ExtendedPersuadeCardInputs[] | undefined>(undefined);

	useStoryblokCards = input<boolean>(true);

	components: typeof Components = Components;
	persuadeCardItems: Signal<
		(PersuadeCardInputs & { normalizedName: string })[]
	>;

	labelPosition = LabelPosition.TopStacked;
	searchText = signal('');
	sortType = signal<string>('');
	disabledText = input<string | undefined>(undefined);

	emptyListTitle = input<string>();
	emptyListContent = input<string>();

	titleInput = computed<SbBlok[]>(() => {
		const title = this.title();
		if (typeof title !== 'string') {
			return (
				title?.map((blok) => ({
					...blok,
					component: blok?.['component'] ?? '',
					maxWidth: 'max-w-screen-xl',
				})) ?? []
			);
		}

		return [];
	});

	sortMap: Record<
		SortType,
		(
			a: { title: string; price: PriceInterface },
			b: { title: string; price: PriceInterface },
		) => number
	> = {
		NAME_ASC: (a, b) => a.title.localeCompare(b.title),
		NAME_DESC: (a, b) => b.title.localeCompare(a.title),
		PRICE_ASC: (a, b) => a.price?.amount - b.price?.amount,
		PRICE_DESC: (a, b) => b.price?.amount - a.price?.amount,
	};

	sortOptions = computed<SortOption[]>(() => {
		const translate = (label: string) => this.translate.instant(label);
		const pricing =
			this.useStoryblokCards() || this.hidePriceFilter()
				? []
				: [
						{
							value: 'PRICE_ASC' as SortType,
							label: translate('global.sort.price-asc'),
						},
						{
							value: 'PRICE_DESC' as SortType,
							label: translate('global.sort.price-desc'),
						},
					];

		return [
			{ value: 'NAME_ASC', label: translate('global.sort.name-asc') },
			{ value: 'NAME_DESC', label: translate('global.sort.name-desc') },
			...pricing,
		];
	});

	orderedList = computed(() => {
		return this.persuadeCardItems()
			.filter((item) => item.normalizedName.includes(this.searchText()))
			.sort(this.sortMap[this.sortType()]);
	});

	pricingTooltipOccupancy = PRICING_TOOLTIP_OCCUPANCY;

	constructor() {
		super();

		this.persuadeCardItems = computed(() => {
			if (this.useStoryblokCards()) {
				if (!this.items()) return [];
				return this.items().map((item) => {
					const title = item.heading[0]?.title as string;
					const overline = item.heading[0]?.overline ?? '';
					return {
						...this.mapButton(item.footer[0]),
						content: item.content as unknown as string,
						headingType: item.heading[0]
							?.headingType as HeadingType,
						images: item.images.map((image) => ({
							src: image.bynder[0].files.transformBaseUrl.url,
							alt: image.imageAlt,
						})),
						overline,
						title,
						normalizedName: `${GeneralHelper.getNormalizedName(overline)} ${GeneralHelper.getNormalizedName(title)}`,
						tags: item.tags,
						iconList: [],
					};
				});
			} else {
				if (!this.persuadeCards()) return [];
				return this.persuadeCards().map((item) => {
					return {
						...item,
						ctaType: 'btn-primary' as CtaType,
						normalizedName: `${GeneralHelper.getNormalizedName(item.overline ?? '')} ${GeneralHelper.getNormalizedName(item.title)}`,
						iconList: item.iconList ?? [],
					};
				});
			}
		});
	}

	mapButton(cardFooter: Button | ButtonList) {
		const button = cardFooter as Button;
		const buttonList = cardFooter as ButtonList;

		if (buttonList?.component === 'buttonList') {
			return {
				ctaTarget: buttonList.buttons?.[0]?.link?.target,
				ctaType: 'btn-primary' as CtaType,
				ctaText: buttonList.buttons?.[0]?.text,
				ctaUrl: buttonList.buttons?.[0]?.link?.cached_url,
				ctaLinkType: buttonList.buttons?.[0]?.link
					?.linktype as LinkType,
				secondaryCtaTarget: buttonList.buttons?.[1]?.link?.target,
				secondaryCtaText: buttonList.buttons?.[1]?.text,
				secondaryCtaUrl: buttonList.buttons?.[1]?.link?.cached_url,
				secondaryCtaLinkType: buttonList.buttons?.[1]?.link
					?.linktype as LinkType,
			};
		} else if (button?.component === 'button') {
			return {
				ctaTarget: button.link?.target,
				ctaText: button.text,
				ctaType: `btn-${button.type}` as CtaType,
				ctaUrl: button.link?.cached_url,
				ctaLinkType: button.link?.linktype as LinkType,
			};
		}
		return null;
	}

	sort(sortType: string): void {
		this.sortType.set(sortType);
	}

	setSearchText(value: unknown) {
		if (typeof value === 'string') {
			this.searchText.set(GeneralHelper.getNormalizedName(value));
		}
	}
}
